/**
 * Tutorial Steps
 * 
 * Defines the educational tutorial sequence that teaches players about:
 * - Hamiltonian Paths
 * - Graph Theory basics
 * - NP-Completeness concepts
 * - Game mechanics
 */

import { TutorialStep } from './types';

export const tutorialSteps: TutorialStep[] = [
  {
    step: 0,
    title: 'Welcome to the NP-Complete Escape Room!',
    explanation: `This game teaches you about one of computer science's most fascinating problems: the Hamiltonian Path Problem.
    
Your goal is simple: visit every room exactly once. But as you'll discover, finding the right path becomes exponentially harder as more rooms are added.

Let's start with a small example to learn the basics.`,
    expectedAction: null,
    highlights: [],
  },
  {
    step: 1,
    title: 'Understanding the Graph',
    explanation: `Look at the graph visualization above. Each circle represents a room, and the lines show which rooms are connected.

🟡 Yellow (pulsing) = Your current location
🔵 Blue = Rooms you've visited
⚪ Gray = Unvisited rooms

The graph shows you the entire "map" of the escape room. This is a visual representation of a mathematical graph - a collection of nodes (rooms) and edges (connections).`,
    expectedAction: null,
    highlights: [],
  },
  {
    step: 2,
    title: 'The Hamiltonian Path Problem',
    explanation: `A Hamiltonian Path is a path through a graph that visits every node exactly once.

In this game:
- You must visit all rooms (nodes)
- You can only visit each room once
- Doors lock behind you (you can't go back)

This is exactly the Hamiltonian Path Problem! Finding such a path is what makes this problem "NP-Complete" - meaning it's computationally very difficult to solve as the graph grows.`,
    expectedAction: null,
    highlights: [],
  },
  {
    step: 3,
    title: 'Making Your First Move',
    explanation: `You're currently in Room 0 (the yellow node). Look at the "Available Exits" section below.

Each exit shows:
- The room number you can move to
- Whether it's locked (already visited)

Try clicking on one of the available exits to move to that room. Choose wisely - remember, you can't go back!`,
    expectedAction: 1, // Expected to move to room 1 (or any valid room)
    highlights: [1, 2, 3], // Highlight available rooms
  },
  {
    step: 4,
    title: 'Understanding Locked Doors',
    explanation: `Great! Notice how the room you just left is now marked as visited (blue) in the graph.

The door behind you is now locked - you can't return to that room. This is a key constraint of the Hamiltonian Path Problem.

In graph theory terms: you're traversing edges, and each edge can only be used once in the direction you're traveling.`,
    expectedAction: null,
    highlights: [],
  },
  {
    step: 5,
    title: 'Planning Ahead',
    explanation: `Here's where strategy becomes important. Before making your next move, look at the graph:

1. Which rooms are still unvisited?
2. Which rooms connect to those unvisited rooms?
3. Could any move trap you in a dead end?

This forward-thinking is essential. In larger graphs, one wrong move early on can make it impossible to visit all rooms.`,
    expectedAction: null,
    highlights: [],
  },
  {
    step: 6,
    title: 'The Undo Feature (Tutorial Only)',
    explanation: `In tutorial mode, you have an "Undo" button that lets you take back moves. This helps you learn without penalty.

In regular gameplay, there's no undo - you must think carefully before each move!

Try using the undo button now to go back one step, then make a different choice.`,
    expectedAction: null,
    highlights: [],
  },
  {
    step: 7,
    title: 'Using Hints',
    explanation: `Stuck? You can use hints! Click the "Hint" button to see the next optimal move.

In tutorial mode, you have unlimited hints. In regular levels, hints are limited (3 per level), so use them wisely.

Hints show you one step of the solution path, helping you learn the strategy.`,
    expectedAction: null,
    highlights: [],
  },
  {
    step: 8,
    title: 'Why This Is NP-Complete',
    explanation: `The Hamiltonian Path Problem is "NP-Complete" because:

1. **Easy to verify**: If someone gives you a path, you can quickly check if it visits all nodes once.

2. **Hard to find**: There's no known efficient algorithm to find the path. You might need to try many possibilities.

3. **Exponential growth**: With 4 rooms, there are manageable paths to check. With 15 rooms? Billions of possibilities!

This is why computer scientists care about this problem - it represents a whole class of difficult computational problems.`,
    expectedAction: null,
    highlights: [],
  },
  {
    step: 9,
    title: 'Complete the Tutorial',
    explanation: `Now it's your turn! Try to visit all remaining rooms.

Remember:
- Plan ahead by looking at the graph
- Use hints if you get stuck
- Use undo to experiment with different strategies

Once you visit all rooms, you'll complete the tutorial and can try the real levels!`,
    expectedAction: null,
    highlights: [],
  },
];

/**
 * Get tutorial step by index
 */
export const getTutorialStep = (step: number): TutorialStep | undefined => {
  return tutorialSteps[step];
};

/**
 * Get total number of tutorial steps
 */
export const getTotalTutorialSteps = (): number => {
  return tutorialSteps.length;
};

/**
 * Generate hint text based on current game state
 */
export const generateHint = (
  currentRoom: number,
  solutionPath: number[],
  _visited: Set<number>
): string => {
  // Find current position in solution path
  const currentIndex = solutionPath.indexOf(currentRoom);
  
  if (currentIndex === -1 || currentIndex === solutionPath.length - 1) {
    return 'You\'re on the right track! Continue exploring.';
  }

  const nextRoom = solutionPath[currentIndex + 1];
  
  return `💡 Hint: Try moving to Room ${nextRoom}. This keeps you on a path that visits all rooms.`;
};